import client from './client'
import type {
  LoginRequest,
  RegisterRequest,
  SessionResponse,
  User,
  UserPreferences,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  VerifyEmailRequest,
  LinkGoogleBeginRequest,
  UnlinkGoogleRequest,
} from '@/types/auth'

export async function login(body: LoginRequest): Promise<SessionResponse> {
  const { data } = await client.post<SessionResponse>('/auth/login', body)
  return data
}

export async function register(body: RegisterRequest): Promise<SessionResponse> {
  const { data } = await client.post<SessionResponse>('/auth/register', body)
  return data
}

export async function logout(): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/logout')
  return data
}

export async function refreshSession(): Promise<SessionResponse> {
  const { data } = await client.post<SessionResponse>('/auth/refresh')
  return data
}

export async function getMe(): Promise<User> {
  const { data } = await client.get<User>('/auth/me')
  return data
}

export async function getPreferences(): Promise<UserPreferences> {
  const { data } = await client.get<UserPreferences>('/auth/me/preferences')
  return data
}

export async function updatePreferences(body: Partial<UserPreferences>): Promise<UserPreferences> {
  const { data } = await client.put<UserPreferences>('/auth/me/preferences', body)
  return data
}

export async function forgotPassword(body: ForgotPasswordRequest): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/forgot-password', body)
  return data
}

export async function resetPassword(body: ResetPasswordRequest): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/reset-password', body)
  return data
}

export async function changePassword(body: ChangePasswordRequest): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/change-password', body)
  return data
}

export async function cancelSubscription(): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/subscription/cancel')
  return data
}

export async function deleteAccount(body: {
  // 2026-05-06 — optional. OAuth-only accounts (no local password)
  // omit it; the backend skips the password verification and relies
  // on the typed "DELETE" confirmation as the sole anti-accident gate.
  current_password?: string
  confirmation: string
}): Promise<{ message: string }> {
  const { data } = await client.delete<{ message: string }>('/auth/account', { data: body })
  return data
}

// ── Email verification (commit 6) ────────────────────────────────────


/** Consume the verification token a user clicked from their email.
 * Called by LoginView's deep-link handler when the URL is
 * /login?action=verify&token=… */
export async function verifyEmail(body: VerifyEmailRequest): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/verify-email', body)
  return data
}

/** Re-issue + resend the verification email. Used by the banner
 * "didn't get the email — resend it" link inside the SPA. */
export async function resendVerificationEmail(): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/resend-verification')
  return data
}


// ── Google OAuth (commit 6) ──────────────────────────────────────────


/** Compute the absolute URL that starts the Google OAuth flow.
 *
 * We do NOT issue an XHR — the browser must navigate top-level so
 * the cookie redirect can land on Google's domain. Returning the
 * URL keeps this API symmetric with the others (caller does
 * window.location.href = …) and lets tests assert on the exact
 * URL shape without intercepting axios.
 *
 * @param next absolute path (matched against the backend allowlist)
 *             where the user lands after a successful sign-in.
 *             Anything outside the allowlist collapses to /home.
 * @param link  when true, generates the link-mode URL — only valid
 *              for an authenticated user who has just confirmed
 *              their password via beginLinkGoogle().
 */
export function googleOAuthLoginUrl(opts: { next?: string; link?: boolean } = {}): string {
  const params = new URLSearchParams()
  if (opts.next) params.set('next', opts.next)
  if (opts.link) params.set('link', 'true')
  // The OAuth bootstrap is on the backend — the user navigates here,
  // backend issues a 302 to Google. Use the same /api prefix as the
  // rest of the auth endpoints so a reverse proxy / CDN config that
  // routes /api/* to the backend keeps working.
  const qs = params.toString()
  return `/api/auth/google/login${qs ? '?' + qs : ''}`
}

/** Verify the user's password and mint the 5-minute link-flow
 * cookie. After the response succeeds, the SPA navigates to
 * googleOAuthLoginUrl({ link: true }). */
export async function beginLinkGoogle(body: LinkGoogleBeginRequest): Promise<{ ok: boolean }> {
  const { data } = await client.post<{ ok: boolean }>('/auth/link-google/begin', body)
  return data
}

/** Send the 1-hour unlink confirmation email (Google-only users
 * who have no local password). */
export async function sendUnlinkConfirmEmail(): Promise<{ message: string }> {
  const { data } = await client.post<{ message: string }>('/auth/account-removal-confirm')
  return data
}

/** Disconnect Google. Hybrid users supply current_password;
 * Google-only users supply confirm_token (from the email link)
 * AND new_password. Returns a SessionResponse — token_version
 * was bumped, this device's cookies were rotated. */
export async function unlinkGoogle(body: UnlinkGoogleRequest): Promise<SessionResponse> {
  const { data } = await client.post<SessionResponse>('/auth/google/unlink', body)
  return data
}
